import React, { useState } from "react";
import { s } from "../lib/theme";
import { LEAVE_TYPES, HOURS_PER_DAY, businessDays, fmt, todayStr } from "../lib/pto";
import { Requests, Attachments, errMsg } from "../lib/api";

export default function LogTimeOff({ users, requests, refreshAll }) {
  const employees = users.filter((u) => u.role !== "admin" && u.active !== false).sort((a, b) => a.name.localeCompare(b.name));

  const [userId, setUserId] = useState(employees[0]?.id || "");
  const [type, setType] = useState("PTO_VACATION");
  const [start, setStart] = useState(todayStr());
  const [end, setEnd] = useState(todayStr());
  const [note, setNote] = useState("");
  const [adminComment, setAdminComment] = useState("");
  const [deduct, setDeduct] = useState(true);
  const [hoursStr, setHoursStr] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const meta = LEAVE_TYPES[type];
  const bDays = businessDays(start, end);
  const defaultHours = bDays * HOURS_PER_DAY;
  const overrideHours = hoursStr.trim() === "" ? null : Number(hoursStr);
  const willDeduct = deduct && meta.deducts;
  const hours = !willDeduct
    ? 0
    : (overrideHours !== null && !isNaN(overrideHours) ? overrideHours : defaultHours);

  const selectedEmp = employees.find((u) => u.id === userId);

  async function submit() {
    setErr("");
    setMsg("");
    if (!userId) { setErr("Pick an employee."); return; }
    if (!start || !end || businessDays(start, end) === 0) {
      setErr("Pick a valid date range (weekdays only).");
      return;
    }
    if (overrideHours !== null && (isNaN(overrideHours) || overrideHours < 0)) {
      setErr("Hours must be zero or greater.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        user_id: userId,
        type,
        start,
        end,
        note: note.trim(),
        deduct_balance: deduct,
        admin_comment: adminComment.trim(),
      };
      if (overrideHours !== null) payload.hours = overrideHours;
      const created = await Requests.adminLog(payload);
      for (const f of pendingFiles) {
        try { await Attachments.upload(created.id, f); }
        catch (ex) { console.warn("attachment upload failed", ex); }
      }
      const filesNote = pendingFiles.length ? ` · ${pendingFiles.length} file${pendingFiles.length === 1 ? "" : "s"} attached` : "";
      setMsg(
        `Logged ${bDays} day(s) of ${meta.label} for ${selectedEmp?.name}` +
        (hours > 0 ? ` (-${hours} hrs from balance)` : "") + filesNote + "."
      );
      setNote("");
      setAdminComment("");
      setHoursStr("");
      setPendingFiles([]);
      await refreshAll();
    } catch (e) {
      setErr(errMsg(e, "Could not log time off."));
    } finally {
      setBusy(false);
    }
  }

  async function removeOne(r) {
    if (!window.confirm(
      `Remove this entry: ${r.user_name} · ${r.type_label} · ${fmt(r.start)}${r.end !== r.start ? " – " + fmt(r.end) : ""}?\n\n` +
      `Note: deleting an approved entry does NOT restore PTO hours. ` +
      `If hours were deducted, adjust the employee balance in the Team tab. Attachments will also be removed.`
    )) return;
    try {
      await Requests.remove(r.id);
      await refreshAll();
    } catch (e) {
      setErr(errMsg(e, "Could not delete entry."));
    }
  }

  const approvedHistory = requests
    .filter((r) => r.status === "approved")
    .sort((a, b) => (b.start || "").localeCompare(a.start || ""));

  return (
    <>
      <div style={s.card} data-testid="logoff-card">
        <div style={s.cardTitle}>Log Time Off (Admin)</div>
        <div style={{ ...s.muted, marginBottom: 12 }}>
          Use this to <strong>backfill PTO already taken</strong> before the system existed, or to record an
          approval that happened offline. Entries are saved as <strong>approved</strong> and show up in History
          and the Coverage calendar. The 21-day notice rule is skipped and no email is sent to the employee.
        </div>

        <div style={s.row2}>
          <div>
            <label style={s.label}>Employee</label>
            <select data-testid="logoff-user" style={s.input} value={userId} onChange={(e) => setUserId(e.target.value)}>
              {employees.map((u) => (
                <option key={u.id} value={u.id}>{u.name} — {u.balance_hours} hrs balance</option>
              ))}
            </select>
          </div>
          <div>
            <label style={s.label}>Type of leave</label>
            <select data-testid="logoff-type" style={s.input} value={type} onChange={(e) => setType(e.target.value)}>
              {Object.entries(LEAVE_TYPES).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={s.row2}>
          <div>
            <label style={s.label}>Start</label>
            <input data-testid="logoff-start" style={s.input} type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label style={s.label}>End</label>
            <input data-testid="logoff-end" style={s.input} type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>

        <div style={s.row2}>
          <div>
            <label style={s.label}>Hours (blank = full days × 8)</label>
            <input
              data-testid="logoff-hours"
              style={s.input}
              type="number"
              step="0.25"
              min="0"
              value={hoursStr}
              onChange={(e) => setHoursStr(e.target.value)}
              placeholder={willDeduct ? `default ${defaultHours}` : "N/A"}
              disabled={!willDeduct}
            />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <label style={{ ...s.label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0, paddingBottom: 12 }}>
              <input
                data-testid="logoff-deduct"
                type="checkbox"
                checked={deduct}
                onChange={(e) => setDeduct(e.target.checked)}
                disabled={!meta.deducts}
              />
              <span>
                Deduct from PTO balance
                {!meta.deducts && <span style={{ color: "#9ca3af" }}> · not applicable</span>}
              </span>
            </label>
          </div>
        </div>

        <label style={s.label}>Note from the employee (optional)</label>
        <textarea data-testid="logoff-note" style={{ ...s.input, height: 50, resize: "vertical" }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What the employee said when they requested it" />

        <label style={s.label}>Admin comment (optional)</label>
        <textarea data-testid="logoff-admin-comment" style={{ ...s.input, height: 50, resize: "vertical" }} value={adminComment} onChange={(e) => setAdminComment(e.target.value)} placeholder="e.g. Backfilled from old paper records" />

        <label style={s.label}>Attach files (optional)</label>
        <input
          data-testid="logoff-files"
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.heic,.heif,.webp,.gif,.ics,.txt,image/*,application/pdf,text/calendar"
          multiple
          onChange={(e) => setPendingFiles(Array.from(e.target.files || []))}
          style={{ fontSize: 12, marginBottom: 4 }}
        />
        <div style={{ fontSize: 11, color: "#9ca3af" }}>
          PDF of physical request form, calendar (.ics), photo, up to 10 MB each (max 8 files).
          {pendingFiles.length > 0 && (
            <span style={{ color: "#0f5132", marginLeft: 6, fontWeight: 600 }}>
              {pendingFiles.length} file{pendingFiles.length === 1 ? "" : "s"} ready
            </span>
          )}
        </div>

        <div style={s.calcRow}>
          <span>{bDays} weekday{bDays === 1 ? "" : "s"}</span>
          {hours > 0
            ? <span>· will deduct <strong>{hours} hrs</strong> from {selectedEmp?.name?.split(" ")[0] || "employee"}&apos;s balance</span>
            : <span>· no PTO will be deducted</span>}
        </div>

        {msg && <div data-testid="logoff-msg" style={s.info}>{msg}</div>}
        {err && <div data-testid="logoff-err" style={s.error}>{err}</div>}
        <button data-testid="logoff-submit" disabled={busy} style={s.btnPrimary} onClick={submit}>
          {busy ? "Saving…" : "Log as Approved"}
        </button>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}>All Approved Entries (most recent first)</div>
        <div style={{ ...s.muted, marginBottom: 8, fontSize: 12 }}>
          Click ✕ to delete an entry. Deleting does <strong>not</strong> automatically restore PTO hours — adjust the employee balance in the Team tab if needed.
        </div>
        {approvedHistory.length === 0 && <div style={s.muted}>No approved entries yet.</div>}
        {approvedHistory.map((r) => (
          <div key={r.id} style={{ ...s.coverRow, alignItems: "center" }}>
            <div style={s.coverDate}>{fmt(r.start)}{r.end !== r.start ? ` – ${fmt(r.end)}` : ""}</div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <strong>{r.user_name}</strong>
              <span style={{ color: "#6b7280", fontSize: 13 }}> · {r.type_label}</span>
              {r.hours > 0 && <span style={{ color: "#6b7280", fontSize: 13 }}> · {r.hours} hrs</span>}
              {r.attachments && r.attachments.length > 0 && (
                <span style={{ color: "#0f5132", fontSize: 12, marginLeft: 6 }}>· 📎 {r.attachments.length}</span>
              )}
              {r.note && <div style={{ fontSize: 12, fontStyle: "italic", color: "#6b7280" }}>&ldquo;{r.note}&rdquo;</div>}
            </div>
            <button
              data-testid={`logoff-remove-${r.id}`}
              style={{ ...s.btnGhost, padding: "5px 10px", fontSize: 12, color: "#b91c1c", borderColor: "#fecaca" }}
              onClick={() => removeOne(r)}
            >
              ✕ Remove
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
